import { gql } from '@apollo/client';
import {
  Box,
  chakra,
  FormControl,
  FormErrorMessage,
  FormLabel,
  GridItem,
  useToast,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select, { OptionProps, SingleValue } from 'react-select';
import { z } from 'zod';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { components, selectOptionBaseSchema } from '~components/ui/Select';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigScheduleSelect_ConfigFragment,
  ConfigScheduleSelect_CustomerFragment,
  useConfigUpdateScheduleMutation,
} from './__generated__/ConfigDisplaySchedule.graphql';

const scheduleOption = selectOptionBaseSchema;

type ScheduleOption = z.TypeOf<typeof scheduleOption>;

const schema = z.object({
  configId: z.string(),
  schedule: scheduleOption.nullish(),
});

type FormValues = z.infer<typeof schema>;

export function ConfigScheduleSelect({
  customer,
  config,
  configStatus,
}: {
  customer: ConfigScheduleSelect_CustomerFragment;
  config: ConfigScheduleSelect_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}) {
  const isSupported = !isNil(config.schedule);

  const options = useMemo<ScheduleOption[]>(
    () =>
      customer.schedules.map<ScheduleOption>((p) => ({
        label: p.title,
        value: p.id,
      })),
    [customer],
  );

  const toast = useToast();
  const currentSchedule = useMemo(
    () => options.find((o) => o.value === config.schedule?.schedule?.id) || null,
    [config, options],
  );

  const {
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      configId: config.id,
      schedule: currentSchedule,
    },
    resolver: zodResolver(schema),
  });

  const [selectedSchedule, setSelectedSchedule] =
    useState<SingleValue<ScheduleOption>>(currentSchedule);

  const [updateSchedule, { loading }] = useConfigUpdateScheduleMutation();

  useEffect(() => {
    setSelectedSchedule(currentSchedule);
  }, [currentSchedule]);

  const handleChange = async (selected: ScheduleOption | null) => {
    try {
      await updateSchedule({
        variables: {
          input: {
            configurationId: config.id,
            scheduleId: selected ? selected.value : null,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
    } catch (err) {
      toast({
        status: 'error',
        title: t('errorUpdatingSchedule'),
      });
    }
  };

  const { verifyUserPermissions } = useAuth();

  const isDisplaySettingsUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);

  return (
    <GridItem
      colSpan={{
        base: 8,
        sm: 6,
        lg: 8,
        xl: 5,
      }}
    >
      {isSupported ? (
        <FormControl isInvalid={Boolean(errors.schedule)}>
          <FormLabel w={'100%'}>
            {t('selectSchedule')}
            <FormLabelPendingIndicator isPending={loading} />
          </FormLabel>
          <Controller
            name="schedule"
            control={control}
            render={({ field }) => (
              <Select
                value={selectedSchedule}
                options={options}
                components={{
                  ...components,
                  Option: ManageScheduleSelectOption,
                }}
                onChange={(selected) => {
                  field.onChange(selected);
                  handleChange(selected);
                }}
                isLoading={isSubmitting}
                placeholder={t('select')}
                isMulti={false}
                escapeClearsValue={false}
                isClearable={true}
                isSearchable={true}
                menuPlacement="auto"
                isDisabled={isDisplaySettingsUpdateDisabled}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name="schedule"
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FormControl>
      ) : (
        <>{t('scheduleNotSupported')}</>
      )}
    </GridItem>
  );
}

export function ManageScheduleSelectOption({
  isSelected,
  innerProps,
  ...rest
}: OptionProps<ScheduleOption, false>) {
  const data = rest.data;

  return (
    <Box
      display="flex"
      alignItems="center"
      paddingX="3.5"
      paddingY="2"
      cursor="pointer"
      color={isSelected ? 'white' : 'blue.800'}
      background={isSelected ? 'blue.400' : 'white'}
      fontSize="md"
      _hover={{
        background: isSelected ? 'blue.400' : 'blue.50',
      }}
      {...innerProps}
    >
      <chakra.span display="inline-block" flex="1">
        {data.label}
      </chakra.span>
    </Box>
  );
}

ConfigScheduleSelect.graphql = {
  fragments: {
    ConfigScheduleSelect_customer: gql`
      fragment ConfigScheduleSelect_customer on Customer {
        id
        schedules {
          id
          title
        }
      }
    `,
    ConfigScheduleSelect_config: gql`
      fragment ConfigScheduleSelect_config on ConfigurationModel {
        id
        schedule {
          schedule {
            id
            title
          }
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateSchedule: gql`
      mutation ConfigUpdateSchedule($input: ConfigurationUpdateScheduleInput!) {
        configurationUpdateSchedule(input: $input) {
          id
        }
      }
    `,
  },
};
